import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { listAccounts } from '@packages/api/endpoints/account.js';
import { getNextPageParam, flattenPages, queryKeys, DEFAULT_LIST_PAGE_SIZE } from '@packages/api';
import { useAuth } from '../lib/auth/useAuth';

/**
 * The accounts the signed-in user is a member of, one page at a time. Callers
 * render the first page and call `fetchNextPage` on demand; never drain it.
 * Administered entities have no membership rows, so they are not listed.
 *
 * The cache key carries the user id because sign-out leaves the query cache
 * intact: a shared browser must not show the previous user's firms.
 */
export function useUserAccounts() {
  const { user } = useAuth();
  const { data, hasNextPage, ...query } = useInfiniteQuery({
    queryKey: [...queryKeys.accounts, user?.id ?? ''],
    queryFn: ({ pageParam }) => listAccounts({ cursor: pageParam, limit: DEFAULT_LIST_PAGE_SIZE }),
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
  });

  const accounts = useMemo(() => flattenPages(data?.pages ?? []), [data]);

  return { ...query, data, hasNextPage, accounts };
}
